"use client";

import {
  LeadCapturePayload,
  LeadCaptureResponse,
} from "@interfaces/leadCapture.types";
import { postLead } from "@services/leadCaptureService";
import { trackLeadCreationFailure } from "@utils/exceptionTracker";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getLeadData } from "@utils/leadDataStorage";
import { getPlatform } from "@utils/platformDetection.client";
import { addUTMParamsToLeadData } from "@utils/utmParams";

export const CALLBACK_BUYING_PERIOD = "Within a month";

type LeadCaptureContext = {
  pageType?: string;
  carAvailability?: string;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  cityName?: string;
  vehicleColor?: string;
  trackingProps?: Record<string, any>;
};

type TrackedLeadData = LeadCapturePayload & {
  color?: string;
  dump_data?: NonNullable<LeadCapturePayload["dump_data"]> & Record<string, any>;
};

type BuildLeadPayloadParams = LeadCaptureContext &
  {
  baseLeadData?: LeadCapturePayload;
  dumpData?: Record<string, any>;
  buyingPeriod?: string;
  loginTypeLabel?: string;
  componentName?: string;
  flowTypeLabel?: string;
};

type TrackingPropsParams = LeadCaptureContext & {
  loginType: string;
  intent: string;
  error?: string;
};

type CaptureLeadParams = LeadCaptureContext &
  {
  baseLeadData?: LeadCapturePayload;
  dumpData?: Record<string, any>;
  buyingPeriod?: string;
  loginTypeLabel: string;
  flowTypeLabel?: string;
  componentName?: string;
  onLeadDataMerged?: (leadData: TrackedLeadData) => void;
};

type PrepareLeadDataForCaptureParams = LeadCaptureContext &
  {
  baseLeadData?: LeadCapturePayload;
  dumpData?: Record<string, any>;
  buyingPeriod?: string;
  loginTypeLabel?: string;
  componentName?: string;
  onLeadDataMerged?: (leadData: TrackedLeadData) => void;
  flowTypeLabel?: string;
};

type SubmitPreparedLeadForCaptureParams = LeadCaptureContext &
  {
  leadData: TrackedLeadData;
  buyingPeriod?: string;
  loginTypeLabel: string;
  componentName?: string;
  flowTypeLabel?: string;
};

type BuildLeadDumpDataParams = {
  pageType?: string;
  loginTypeLabel?: string;
  vehicleColor?: string;
  dumpData?: Record<string, any>;
  flowTypeLabel?: string;
};

type MissingPhoneTrackingParams = LeadCaptureContext & {
  pageType?: string;
  loginTypeLabel?: string;
  componentName?: string;
  intent?: string;
  exceptionName?: string;
  flowTypeLabel?: string;
};

type SubmitLeadCaptureParams = {
  leadData: TrackedLeadData;
  addUtmParams?: boolean;
} & MissingPhoneTrackingParams;

const buildLeadTrackingProps = ({
  carAvailability,
  brandName,
  modelName,
  variantName,
  vehicleColor,
  cityName,
  trackingProps,
  loginType,
  intent,
  error,
}: TrackingPropsParams) => ({
  car_availability: carAvailability,
  vehicle_make: brandName,
  vehicle_model: modelName,
  vehicle_variant: variantName,
  vehicle_color: vehicleColor,
  city_name: cityName,
  ...trackingProps,
  login_type: loginType,
  intent,
  error,
});

const getLeadDumpPageUrl = () => {
  if (typeof window !== "undefined" && typeof window.location?.href === "string") {
    return window.location.href;
  }

  return process.env.NEXT_PUBLIC_FRONTEND_HOST || "";
};

const getLeadDumpUtmTerm = (pageUrl: string) => {
  if (typeof window !== "undefined") {
    return new URLSearchParams(window.location?.search || "").get("utm_term") || "";
  }

  if (!pageUrl) {
    return "";
  }

  try {
    return new URL(pageUrl).searchParams.get("utm_term") || "";
  } catch {
    return "";
  }
};

export const buildLeadDumpData = ({
  pageType,
  loginTypeLabel,
  vehicleColor,
  dumpData,
  flowTypeLabel
}: BuildLeadDumpDataParams) => {
  const pageUrl = getLeadDumpPageUrl();
  const utmTerm = getLeadDumpUtmTerm(pageUrl);

  return {
    mx_Platform: getPlatform(),
    mx_Page_Type_Web: pageType,
    ...(loginTypeLabel ? { mx_Login_Type: loginTypeLabel } : {}),
    ...(flowTypeLabel? {mx_Latest_Lead_Origin: flowTypeLabel} :{}),
    mx_URL: pageUrl,
    mx_UTM_Term: utmTerm,
    mx_color: vehicleColor || "",
    ...dumpData,
  };
};

const mergeTrackedLeadData = (
  existingLeadData: Record<string, any>,
  nextLeadData: TrackedLeadData
): TrackedLeadData => {
  const mergedLeadData: Record<string, any> = { ...existingLeadData };

  Object.entries(nextLeadData).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }

    if (key === "phone" && typeof value === "string" && !value.trim()) {
      return;
    }

    mergedLeadData[key] = value;
  });

  return mergedLeadData as TrackedLeadData;
};

const buildLeadPayload = async ({
  cityName,
  brandName,
  modelName,
  variantName,
  vehicleColor,
  pageType,
  loginTypeLabel,
  baseLeadData,
  dumpData,
  buyingPeriod,
  flowTypeLabel
}: BuildLeadPayloadParams): Promise<TrackedLeadData> => {
  let leadData: TrackedLeadData = {
    city: cityName,
    make: brandName || "",
    model: modelName || "",
    variant: variantName || "",
    color: vehicleColor || "",
    ...baseLeadData,
  };

  if (buyingPeriod) {
    leadData.buying_period = buyingPeriod;
  }

  leadData.dump_data = buildLeadDumpData({
    pageType,
    loginTypeLabel,
    vehicleColor: leadData.color || "",
    dumpData,
    flowTypeLabel
  });

  const existingLeadData = await getLeadData();
  const mergedLeadData = mergeTrackedLeadData(existingLeadData, leadData);
  return mergedLeadData;
};

const getCaptureFailureLoginType = (
  loginTypeLabel: string,
  componentName?: string
) =>
  (componentName
    ? `Capture ${loginTypeLabel} failed in ${componentName}`
    : `Capture ${loginTypeLabel} failed`);

const getErrorLoginType = (
  loginTypeLabel: string,
  componentName?: string
) =>
  (componentName
    ? `Error ${loginTypeLabel} failed in ${componentName}`
    : `Error ${loginTypeLabel} failed`);

const trackMissingPhoneForLeadCapture = async ({
  pageType,
  carAvailability,
  brandName,
  modelName,
  variantName,
  cityName,
  vehicleColor,
  trackingProps,
  loginTypeLabel,
  componentName,
  intent = "",
  exceptionName = "missing_phone_for_lead_capture",
}: MissingPhoneTrackingParams) => {
  const missingPhoneLoginType = loginTypeLabel
    ? `Missing phone for ${loginTypeLabel}`
    : "Missing phone for lead capture";

  await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
    pageType,
    additionalProps: {
      exception_type: "data_issue",
      exception_name: exceptionName,
      component_name: componentName,
      ...buildLeadTrackingProps({
        carAvailability,
        brandName,
        modelName,
        variantName,
        vehicleColor,
        cityName,
        trackingProps,
        loginType: missingPhoneLoginType,
        intent,
      }),
    },
  });
};

export const prepareLeadDataForCapture = async ({
  pageType,
  carAvailability,
  brandName,
  modelName,
  variantName,
  cityName,
  vehicleColor,
  trackingProps,
  loginTypeLabel,
  componentName,
  baseLeadData,
  dumpData,
  buyingPeriod,
  onLeadDataMerged,
  flowTypeLabel,
}: PrepareLeadDataForCaptureParams): Promise<TrackedLeadData | null> => {
  const leadData = await buildLeadPayload({
    cityName,
    brandName,
    modelName,
    variantName,
    vehicleColor,
    pageType,
    trackingProps,
    loginTypeLabel,
    componentName,
    baseLeadData,
    dumpData,
    buyingPeriod,
    flowTypeLabel
  });

  const intent = buyingPeriod || leadData.buying_period || "";
  onLeadDataMerged?.(leadData);

  if (!leadData.phone) {
    await trackMissingPhoneForLeadCapture({
      pageType,
      carAvailability,
      brandName,
      modelName,
      variantName,
      cityName,
      vehicleColor: vehicleColor || leadData.color || "",
      trackingProps,
      loginTypeLabel,
      componentName,
      intent,
    });
    return null;
  }

  return leadData;
};

export const submitLeadCapture = async ({
  leadData,
  addUtmParams = true,
  pageType,
  carAvailability,
  brandName,
  modelName,
  variantName,
  cityName,
  vehicleColor,
  trackingProps,
  loginTypeLabel,
  componentName,
  intent,
  exceptionName,
  flowTypeLabel
}: SubmitLeadCaptureParams): Promise<LeadCaptureResponse | null> => {
  if (!leadData.phone) {
    await trackMissingPhoneForLeadCapture({
      pageType,
      carAvailability,
      brandName: brandName || leadData.make,
      modelName: modelName || leadData.model,
      variantName: variantName || leadData.variant,
      cityName: cityName || leadData.city,
      vehicleColor: vehicleColor || leadData.color || "",
      trackingProps,
      loginTypeLabel,
      componentName,
      intent: intent || leadData.buying_period || "",
      exceptionName,
    });
    return null;
  }

  return postLead(
    addUtmParams ? addUTMParamsToLeadData(leadData) : leadData
  );
};

export const submitPreparedLeadForCapture = async ({
  pageType,
  carAvailability,
  brandName,
  modelName,
  variantName,
  cityName,
  vehicleColor,
  trackingProps,
  leadData,
  buyingPeriod,
  loginTypeLabel,
  componentName,
  flowTypeLabel
}: SubmitPreparedLeadForCaptureParams): Promise<LeadCaptureResponse | null> => {
  const intent = buyingPeriod || leadData.buying_period || "";

  try {
    const leadResponse = await submitLeadCapture({
      leadData,
      pageType,
      carAvailability,
      brandName,
      modelName,
      variantName,
      cityName,
      vehicleColor,
      trackingProps,
      loginTypeLabel,
      componentName,
      intent,
      flowTypeLabel
    });

    if (!leadResponse) {
      return null;
    }

    if (!leadResponse.success) {
      console.error("Lead capture failed.", leadResponse);
      await trackLeadCreationFailure(
        pageType,
        buildLeadTrackingProps({
          carAvailability,
          brandName,
          modelName,
          variantName,
          vehicleColor,
          cityName,
          trackingProps,
          loginType: getCaptureFailureLoginType(loginTypeLabel, componentName),
          intent,
        })
      );
      return leadResponse;
    }

    const leadCreated = !!leadResponse?.data?.is_created;
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType,
      additionalProps: {
        action_type: leadCreated ? "lead_created" : "lead_updated",
        ...buildLeadTrackingProps({
          carAvailability,
          brandName,
          modelName,
          variantName,
          vehicleColor,
          cityName,
          trackingProps,
          loginType: loginTypeLabel,
          intent,
        }),
      },
    });
    return leadResponse;
  } catch (error) {
    console.error("Error in postLead API call:", error);
    await trackLeadCreationFailure(
      pageType,
      buildLeadTrackingProps({
        carAvailability,
        brandName,
        modelName,
        variantName,
        vehicleColor,
        cityName,
        trackingProps,
        loginType: getErrorLoginType(loginTypeLabel, componentName),
        intent,
        error: (error as Error).message,
      })
    );
    return null;
  }
};

export const captureLead = async ({
  pageType,
  carAvailability,
  brandName,
  modelName,
  variantName,
  cityName,
  vehicleColor,
  trackingProps,
  loginTypeLabel,
  componentName,
  baseLeadData,
  dumpData,
  buyingPeriod,
  onLeadDataMerged,
  flowTypeLabel
}: CaptureLeadParams): Promise<boolean> => {
  const leadData = await prepareLeadDataForCapture({
    pageType,
    carAvailability,
    brandName,
    modelName,
    variantName,
    cityName,
    vehicleColor,
    trackingProps,
    loginTypeLabel,
    componentName,
    baseLeadData,
    dumpData,
    buyingPeriod,
    onLeadDataMerged,
    flowTypeLabel
  });

  if (!leadData) {
    return false;
  }

  const leadResponse = await submitPreparedLeadForCapture({
    pageType,
    carAvailability,
    brandName,
    modelName,
    variantName,
    cityName,
    vehicleColor,
    trackingProps,
    leadData,
    buyingPeriod,
    loginTypeLabel,
    componentName,
  });
  return !!leadResponse?.success;
};
